import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { useDebounce } from '@uidotdev/usehooks'
import { Modal } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useInView } from 'react-intersection-observer'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import ItemsWithLoading from '@/components/Atoms/ItemsWithLoading/ItemsWithLoading'
import Loader from '@/components/Atoms/Loader/Loader'
import FeedSearchTerm from '@/components/Feed/FeedSearchTerm/FeedSearchTerm'
import Filters from '@/components/Filters/Filters'
import ResetFiltersButton from '@/components/Filters/ResetFiltersButton/ResetFiltersButton'
import SelectedFilters from '@/components/Filters/SelectedFilters/SelectedFilters'
import { DELAY, RAW_SEARCH_SIZE } from '@/constants'
import useNomenclatureEditorSearch from '@/hooks/search/useNomenclatureEditorSearch'
import { filtersService } from '@/services/filters/FiltersService'
import { useNomenclatureEditorFeedStore } from '@/stores/feed/nomenclatureEditorFeedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { AntdTreeType, ApiErrorDetail, CategoryInterface } from '@/types'
import api from '@/utils/api/api'
import {
  fetchCategoriesTree,
  fetchNomenclatureGroupsTree,
} from '@/utils/api/fetch/fetchEntities'
import { getCarriedCharacteristics } from '@/utils/search/characteristics/carriedCharacteristics'
import convertToAntdFormatTree from '@/utils/tree/antd/convertToAntdFormatTree'

import {
  EDITOR_COLUMN_WIDTHS,
  getTanstackColumns,
} from './NomenclatureEditorFeed.config'
import cn from './NomenclatureEditorFeed.module.scss'

interface SuggestionItem {
  id: string
  code: number
  description: string
}

interface SuggestionGroup {
  group_type: string
  items: SuggestionItem[]
}

type SearchTip = SuggestionItem & { group_type: string }

function NomenclatureEditorFeed() {
  const [searchParams] = useSearchParams()
  const [searchValue, setSearchValue] = useState<string>(
    searchParams.get('query') || ''
  )
  const [tips, setTips] = useState<SearchTip[]>([])
  const [searchInputFocused, setSearchInputFocused] = useState<boolean>(false)
  const [categoriesTree, setCategoriesTreeState] = useState<
    CategoryInterface[]
  >([])
  const [antdCategoriesTree, setAntdCategoriesTree] = useState<AntdTreeType[]>(
    []
  )
  const [antdNomenclatureGroupsTree, setAntdNomenclatureGroupsTree] = useState<
    AntdTreeType[]
  >([])
  const [openFilters, setOpenFilters] = useState<boolean>(false)

  const debouncedSearchValue = useDebounce(searchValue, DELAY)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tableInnerRef = useRef<HTMLDivElement>(null)
  const originalHeaderRef = useRef<HTMLDivElement>(null)
  const floatingHeaderRef = useRef<HTMLDivElement>(null)
  const isFloatingVisibleRef = useRef(false)
  const isInitialFetched = useRef(false)
  const isInitialFiltersSaved = useRef(false)

  const {
    setNomenclatureGroupsTree,
    setCategoriesTree,
    nomenclatureGroupsTree: globalNomenclatureGroupsTree,
    categoriesTree: globalCategoriesTree,
  } = useTreeTilesStore()

  const {
    currentlySearchableCategories,
    currentlySearchableBrands,
    currentlySearchableNomenclatureGroupId,
    currentlySearchableCharacteristics,
    currentlySearchableParents,
    withLeftovers,
    withDiscontinued,
    searchTerm,
    searchError,
    initialFilters,
  } = useSearchStore((state) => state)

  const { selectedOrganisation } = useUserInfoStore((state) => state)

  const { products, productsLoading, pagination, paginatedTableLoading } =
    useNomenclatureEditorFeedStore((state) => state)

  const search = useNomenclatureEditorSearch()

  const tableColumns = useMemo(
    () =>
      getTanstackColumns({
        categoriesTree,
        antdCategoriesTree,
        nomenclatureGroupsTree: globalNomenclatureGroupsTree,
        antdNomenclatureGroupsTree,
      }),
    [
      categoriesTree,
      antdCategoriesTree,
      globalNomenclatureGroupsTree,
      antdNomenclatureGroupsTree,
    ]
  )

  const table = useReactTable({
    data: products,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => 90,
    overscan: 30,
    scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()

  const gridColumns = useMemo(
    () =>
      tableColumns
        .map((col) => (col.id && EDITOR_COLUMN_WIDTHS[col.id]) || '1fr')
        .join(' '),
    [tableColumns]
  )

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
  })

  useEffect(() => {
    const el = originalHeaderRef.current
    if (!el) return

    const navHeader = document.querySelector('header')

    const setVisible = (visible: boolean) => {
      const floatingEl = floatingHeaderRef.current
      if (!floatingEl || visible === isFloatingVisibleRef.current) return
      isFloatingVisibleRef.current = visible
      if (visible) {
        floatingEl.classList.remove(cn.floatingHeaderHide)
        floatingEl.classList.add(cn.floatingHeaderVisible)
      } else {
        floatingEl.classList.remove(cn.floatingHeaderVisible)
        floatingEl.classList.add(cn.floatingHeaderHide)
      }
    }

    const updatePosition = () => {
      const floatingEl = floatingHeaderRef.current
      if (!floatingEl) return
      const top = navHeader?.getBoundingClientRect().bottom ?? 0
      const tableRect = tableInnerRef.current?.getBoundingClientRect()
      floatingEl.style.top = `${top}px`
      floatingEl.style.left = `${tableRect?.left ?? 0}px`
      floatingEl.style.width = `${tableRect?.width ?? 0}px`
    }
    updatePosition()

    const getStickyOffset = () => navHeader?.getBoundingClientRect().bottom ?? 0

    const createObserver = () => {
      const offset = getStickyOffset()
      return new IntersectionObserver(
        ([entry]) => setVisible(!entry.isIntersecting),
        { threshold: 0, rootMargin: `-${offset}px 0px 0px 0px` }
      )
    }

    let observer = createObserver()
    observer.observe(el)

    const navResizeObserver = new ResizeObserver(() => {
      updatePosition()
      observer.disconnect()
      observer = createObserver()
      observer.observe(el)
    })
    if (navHeader) navResizeObserver.observe(navHeader)

    const resizeObserver = new ResizeObserver(updatePosition)
    if (navHeader) resizeObserver.observe(navHeader)
    if (tableInnerRef.current) resizeObserver.observe(tableInnerRef.current)
    window.addEventListener('scroll', updatePosition, { passive: true })

    return () => {
      observer.disconnect()
      resizeObserver.disconnect()
      navResizeObserver.disconnect()
      window.removeEventListener('scroll', updatePosition)
    }
  }, [])

  const hasMore = products.length < (pagination.total || 0)

  useEffect(() => {
    if (
      inView &&
      !productsLoading &&
      !paginatedTableLoading &&
      hasMore &&
      products.length >= 10
    ) {
      const nextPage = pagination.current || 1

      search({
        query: searchTerm,
        categories: currentlySearchableCategories,
        brands: currentlySearchableBrands,
        parents: currentlySearchableParents,
        nomenclatureGroupId: currentlySearchableNomenclatureGroupId,
        page: nextPage,
        characteristicFilters: currentlySearchableCharacteristics,
        withLeftovers,
        withDiscontinued,
        triggerNavigation: false,
      })
    }
  }, [inView])

  const hasDivergedFromInitial = useMemo(() => {
    if (!initialFilters) return false

    const compareIds = (arr1: { id: string }[], arr2: { id: string }[]) =>
      [...(arr1 || [])]
        .map((i) => i.id)
        .sort()
        .join(',') !==
      [...(arr2 || [])]
        .map((i) => i.id)
        .sort()
        .join(',')

    if (withDiscontinued === false) return true
    if (currentlySearchableCharacteristics.length > 0) return true
    // if (searchTerm.trim() !== initialFilters.searchTerm.trim()) return true
    if (compareIds(currentlySearchableBrands, initialFilters.brands))
      return true
    if (compareIds(currentlySearchableCategories, initialFilters.categories))
      return true
    if (compareIds(currentlySearchableParents, initialFilters.parents))
      return true

    return false
  }, [
    withDiscontinued,
    currentlySearchableCharacteristics,
    searchTerm,
    currentlySearchableBrands,
    currentlySearchableCategories,
    currentlySearchableParents,
    initialFilters,
  ])

  const handleResetFilters = () => {
    search({
      query: initialFilters?.searchTerm ?? '',
      categories: initialFilters?.categories ?? [],
      brands: initialFilters?.brands ?? [],
      parents: initialFilters?.parents ?? [],
      nomenclatureGroupId: currentlySearchableNomenclatureGroupId,
      characteristicFilters: [],
      page: 0,
      withLeftovers: false,
      withDiscontinued: true,
      triggerNavigation: true,
    })
  }

  useEffect(() => {
    const fetchMetadata = async () => {
      if (isInitialFetched.current) return
      isInitialFetched.current = true
      try {
        const [groupsTree, catsTree] = await Promise.all([
          globalNomenclatureGroupsTree.length === 0
            ? fetchNomenclatureGroupsTree()
            : Promise.resolve(globalNomenclatureGroupsTree),
          globalCategoriesTree.length === 0
            ? fetchCategoriesTree()
            : Promise.resolve(globalCategoriesTree),
        ])
        if (globalNomenclatureGroupsTree.length === 0)
          setNomenclatureGroupsTree(groupsTree)
        if (globalCategoriesTree.length === 0) setCategoriesTree(catsTree)
        const treeUIConfig = {
          classNames: { treeNode: cn.treeNode },
          allSelectable: false,
          inTreeSelect: true,
        }
        setAntdNomenclatureGroupsTree(
          convertToAntdFormatTree({ nodes: groupsTree, ...treeUIConfig })
        )
        setAntdCategoriesTree(
          convertToAntdFormatTree({ nodes: catsTree, ...treeUIConfig })
        )
        setCategoriesTreeState(catsTree)
      } catch {
        toast.error('Ошибка загрузки справочников')
      }
    }
    fetchMetadata()
  }, [])

  useEffect(() => {
    const filtersFromUrl = filtersService.url.parseAllFiltersSync(
      searchParams,
      false
    )
    const groupId = searchParams.get('groupId') || ''

    const isFirstLoad = !isInitialFiltersSaved.current
    const shouldSaveInitialFilters =
      isFirstLoad && !useSearchStore.getState().initialFilters
    if (isFirstLoad) {
      isInitialFiltersSaved.current = true
    }

    const parentsFromUrl = isFirstLoad && filtersFromUrl.parents.length > 0

    search({
      query: filtersFromUrl.query,
      productCodes: filtersFromUrl.productCodes,
      categories: filtersFromUrl.categories,
      brands: filtersFromUrl.brands,
      parents: filtersFromUrl.parents,
      nomenclatureGroupId: groupId,
      page: filtersFromUrl.page,
      withLeftovers: false,
      withDiscontinued: filtersFromUrl.withDiscontinued,
      matchUniformSlottedCharacteristics:
        filtersFromUrl.matchUniformSlottedCharacteristics,
      characteristicFilters: filtersFromUrl.characteristics,
      triggerNavigation: false,
      isInitial: shouldSaveInitialFilters,
      parentsFromUrl,
    })
  }, [searchParams])

  useEffect(() => {
    if (debouncedSearchValue && searchInputFocused) {
      api
        .get('/nomenclatures/suggestions', {
          params: {
            query: debouncedSearchValue,
            limit: RAW_SEARCH_SIZE,
            withLeftovers: false,
          },
        })
        .then((response) => {
          const data = response.data.result || response.data
          const allItems =
            data.groups?.flatMap((g: SuggestionGroup) =>
              g.items.map((item) => ({
                ...item,
                group_type: g.group_type,
              }))
            ) || []
          setTips(allItems.slice(0, 10))
        })
        .catch(() => setTips([]))
    } else {
      setTips([])
    }
  }, [debouncedSearchValue, searchInputFocused])

  const handleSearchNomenclature = () => {
    const q = searchValue
    setTips([])
    setSearchInputFocused(false)
    search({
      query: q,
      categories: [],
      nomenclatureGroupId: '',
      brands: [],
      parents: [],
      page: 0,
      withLeftovers: false,
      withDiscontinued: true,
      characteristicFilters: getCarriedCharacteristics(),
      triggerNavigation: true,
    })
    setSearchValue('')
  }

  const [, contextHolder] = Modal.useModal()

  return (
    <section className={cn.nomenclatureEditorFeed}>
      {contextHolder}
      <div className={cn.searchBarContainer}>
        <div className={cn.inputContainer}>
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setSearchInputFocused(true)}
            onBlur={() => setTimeout(() => setSearchInputFocused(false), DELAY)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearchNomenclature()
            }}
            placeholder="Введите артикул, код или название..."
            ref={searchInputRef}
          />
          {tips.length > 0 && searchInputFocused && (
            <div className={cn.tips}>
              {tips.map((tip, index) => (
                <div
                  key={index}
                  onClick={() => {
                    const p: Parameters<typeof search>[0] = {
                      query: '',
                      categories: [],
                      brands: [],
                      parents: [],
                      nomenclatureGroupId: '',
                      page: 0,
                      withLeftovers: false,
                      withDiscontinued: true,
                      characteristicFilters: getCarriedCharacteristics(),
                      triggerNavigation: true,
                    }
                    if (tip.group_type === 'CATEGORY')
                      p.categories = [
                        { id: tip.id, description: tip.description, count: 0 },
                      ]
                    else if (tip.group_type === 'BRAND')
                      p.brands = [
                        { id: tip.id, description: tip.description, count: 0 },
                      ]
                    else p.productCodes = [tip.code]
                    search(p)
                    setSearchValue('')
                  }}
                >
                  <span>{tip.description}</span>
                  <small className={cn.tipGroupType}>({tip.group_type})</small>
                </div>
              ))}
            </div>
          )}
        </div>
        <button onClick={handleSearchNomenclature}>Найти</button>
        <button onClick={() => setOpenFilters(true)}>Фильтры</button>
        <Filters open={openFilters} setOpen={setOpenFilters} />
      </div>

      <div className={cn.feed}>
        <FeedSearchTerm />
        <div className={cn.filtersBar}>
          <SelectedFilters />
          <ResetFiltersButton
            selectedOrganisation={selectedOrganisation}
            onClick={handleResetFilters}
            disabled={!hasDivergedFromInitial}
          />
        </div>

        <ItemsWithLoading
          items={products}
          loading={productsLoading && products.length === 0}
          loadingError={!!searchError}
          loaderPaddingTop={100}
          error={
            <div
              className={[
                cn.emptyOrErrorMessageContainer,
                typeof searchError === 'object' ? cn.detailedError : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {(() => {
                if (typeof searchError === 'object' && searchError !== null) {
                  const err = searchError as {
                    message: string
                    details?: ApiErrorDetail[]
                    traceId?: string
                  }
                  return (
                    <>
                      <div className={cn.errorMainMessage}>{err.message}</div>

                      {err.details && err.details.length > 0 && (
                        <div className={cn.errorDetailsBlock}>
                          <div className={cn.errorDetailsTitle}>
                            Подробности:
                          </div>
                          {err.details.map((detail, index) => (
                            <div key={index} className={cn.errorDetailItem}>
                              <div className={cn.detailText}>
                                {detail.message}
                              </div>
                              {detail.context && (
                                <div className={cn.detailContext}>
                                  Поставщик: {detail.context.key.supplierId} |
                                  Склад: {detail.context.key.storeCode} | Товар:{' '}
                                  {detail.context.key.productCode}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {err.traceId && (
                        <div className={cn.traceId}>traceId: {err.traceId}</div>
                      )}
                    </>
                  )
                }

                return (
                  <span className={cn.searchErrorText}>
                    {String(searchError)}
                  </span>
                )
              })()}
            </div>
          }
          empty={
            <div className={cn.emptyMessage}>
              <span>Совпадений не найдено</span>
            </div>
          }
        >
          <div ref={tableContainerRef} className={cn.table}>
            <div
              ref={tableInnerRef}
              className={cn.virtualTableContainer}
              style={{ '--grid-columns': gridColumns }}
            >
              <div ref={originalHeaderRef} className={cn.virtualTableHeader}>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <div key={header.id} className={cn.headerCell}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>
                  ))
                )}
              </div>

              {createPortal(
                <div ref={floatingHeaderRef} className={cn.floatingHeader}>
                  {table.getHeaderGroups().map((headerGroup) =>
                    headerGroup.headers.map((header) => (
                      <div key={header.id} className={cn.headerCell}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    ))
                  )}
                </div>,
                document.body
              )}

              <div
                className={cn.virtualList}
                style={{
                  '--virtual-total-size': `${rowVirtualizer.getTotalSize()}px`,
                }}
              >
                {virtualItems.map((virtualRow) => {
                  const row = rows[virtualRow.index]
                  return (
                    <div
                      key={virtualRow.key}
                      className={cn.virtualRow}
                      style={{
                        '--virtual-row-size': `${virtualRow.size}px`,
                        '--virtual-row-start': `${virtualRow.start - rowVirtualizer.options.scrollMargin}px`,
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <div key={cell.id} className={cn.virtualCell}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <div ref={loadMoreRef} className={cn.loadMoreTrigger}>
            {paginatedTableLoading && <Loader size={20} />}
            {!hasMore && products.length > 0 && (
              <span className={cn.endOfListText}>Все товары загружены</span>
            )}
          </div>
        </ItemsWithLoading>
      </div>
    </section>
  )
}

export default NomenclatureEditorFeed
